// src/pages/CreditReport.jsx
import { useState, useEffect } from "react";
import CreditReportInstructionsModal from "../components/CreditReportInstructionsModal";
import { primaryDebtor, secondaryFilingDebtor } from '../../utils/debtors';

const CREDIT_BUREAUS = ['equifax', 'experian', 'transunion'];


const API_URL = process.env.REACT_APP_API_URL;
export default function CreditReportPage( {spouseIsFiling = false} ) {
  const [showModal, setShowModal] = useState(false);
  const [uploads, setUploads] = useState({});
  const [status, setStatus] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const caseId = localStorage.getItem("clientCaseId");
  const [caseData, setCaseData] = useState(null);
  const [uploadedReports, setUploadedReports] = useState([]);
  const [visibleErrorKey, setVisibleErrorKey] = useState(null);

  useEffect(() => {
    const fetchUploadedReports = async () => {
      try {
        const token = localStorage.getItem("clientToken");
        const res = await fetch(`${API_URL}/case/${caseId}/credit-report/uploads/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          console.log(data)
          const grouped = {};
          data.forEach((report) => {
            const key = report.is_spouse ? `spouse_${report.bureau}` : report.bureau;
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(report);
          });
          setStatus(grouped);
        }
      } catch (err) {
        console.error("Failed to load uploaded credit reports:", err);
      }
    };
  
    fetchUploadedReports();
  }, [caseId]);


  useEffect(() => {
    const fetchCase = async () => {
      try {
        const token = localStorage.getItem("clientToken");// or pass it via props
        const res = await fetch(`${process.env.REACT_APP_API_URL}/case/${caseId}/credit-report/case-details/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setCaseData(data);
      } catch (err) {
        console.error("Error fetching case:", err);
      } finally {
        setLoading(false);
      }
    };
  
    fetchCase();
  }, []);

  const filingPrimaryDebtor = primaryDebtor(caseData?.debtors, { filingOnly: true });
  const spouseDebtor = secondaryFilingDebtor(caseData?.debtors);

  const handleFileChange = (bureau, file, isSpouse = false) => {
      if (file && file.type !== 'application/pdf') {
        alert('Only PDF files are allowed.');
        return;
      }

      const key = isSpouse ? `spouse_${bureau.toLowerCase()}` : bureau.toLowerCase();
      setUploads(prev => ({ ...prev, [key]: file }));
  };

  const uploadFile = async (key, file) => {
    const formData = new FormData();
    formData.append("file", file);
  
    const isSpouse = key.startsWith("spouse_");
    formData.append("is_spouse", isSpouse ? "true" : "false");
  
    const bureau = key.replace("spouse_", "").toLowerCase();  // "equifax", "experian", etc.
    const token = localStorage.getItem("clientToken");
    const caseId = localStorage.getItem("clientCaseId");
  
    try {
      const res = await fetch(
        `${API_URL}/case/${caseId}/upload-credit-report/${bureau}/`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        }
      );
  
      const result = await res.json();
  
      setStatus(prev => ({
        ...prev,
        [key]: {
          result: res.ok ? "success" : "failed",
          error: result?.error || null,
          filename: file.name,
          file_url: result?.file_url || null,  // only if your backend returns it
        },
      }));
    } catch (err) {
      console.error("Upload failed:", err);
      setStatus(prev => ({
        ...prev,
        [key]: {
          result: "failed",
          error: err.message || "Unknown error",
          filename: file.name,
          file_url: null,
        },
      }));
    }
  };

  const handleSubmit = async () => {
    setUploading(true);
    setUploadComplete(false);
  
    const entries = Object.entries(uploads);
    console.log(`Entries: ${entries}`)
    if (entries.length === 0) {
      alert("Please upload at least one credit report.");
      setUploading(false);  // <-- ensure spinner stops
      return;
    }
  
    try {
      for (const [key, file] of entries) {
        if (file) await uploadFile(key, file);
      }
      setUploadComplete(true);
    } catch (error) {
      console.error("Upload error:", error);
      alert("Upload failed.");
    } finally {
      setUploading(false);
    }
  };


  const renderUpload = (bureau, isSpouse = false) => {
    const key = isSpouse ? `spouse_${bureau}` : bureau;
    const label = isSpouse
  ? `Spouse ${bureau.charAt(0).toUpperCase() + bureau.slice(1)}`
  : bureau.charAt(0).toUpperCase() + bureau.slice(1);

  
    return (
      
      <div
        key={key}
        className="w-full bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-5"
      >
        <label className="block text-sm font-semibold text-gray-800 mb-2">{label}</label>
        <input
          type="file"
          accept="application/pdf"
          onChange={(e) => handleFileChange(bureau, e.target.files[0], isSpouse)}
          className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
        />
        {uploads[key] && (
          <p className="mt-2 text-xs text-green-700">✅ Uploaded: {uploads[key].name}</p>
        )}
        <p className="mt-1 text-xs text-gray-500">PDF files only.</p>
        {status[key] && Array.isArray(status[key]) && (
          <div className="mt-2 space-y-1">
            {status[key].map((report) => {
              const isSuccess = report.parsing_success !== false;
              return (
                <div
                  key={report.id}
                  className={`flex justify-between items-center text-sm ${
                    isSuccess ? "text-green-700" : "text-red-600"
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <span>{isSuccess ? "✅" : "❌"} {report.filename}</span>
                    {!isSuccess && (
                      <div className="relative">
                        <button
                          className="text-xs underline"
                          onClick={() =>
                            setVisibleErrorKey(visibleErrorKey === report.id ? null : report.id)
                          }
                        >
                          ℹ️
                        </button>
                        {visibleErrorKey === report.id && (
                          <div className="absolute z-10 mt-1 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                            {report.error_message || "Unknown error"}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {report.file_url && (
                    <a
                      href={report.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs underline"
                    >
                      Download
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    setShowModal(true);
  }, []);

  return (
    <div className="mx-auto w-full max-w-screen-xl px-2 sm:px-4 md:px-6 lg:px-8">
      {showModal && (
        <CreditReportInstructionsModal onClose={() => setShowModal(false)} />
      )}

      <h1 className="text-2xl font-bold text-indigo-700 mb-6">Upload Your Credit Reports</h1>

      <p className="text-gray-700 mb-4">
        Once you’ve downloaded your reports from annualcreditreport.com, upload them here. If your upload comes back as failed, then the likely culprit is the PDF was formatted incorrectly. Try again to print the PDF according to the instructions.
        You must include at least one of the following:
      </p>

      {filingPrimaryDebtor && (
          <>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              {filingPrimaryDebtor.first_name}'s Credit Reports
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 xl:gap-8 mb-6">
              {CREDIT_BUREAUS.map(bureau => renderUpload(bureau))}
            </div>
          </>
        )}

        {spouseDebtor && (
          <>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              Spouse: {spouseDebtor.first_name}'s Credit Reports
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 xl:gap-8 mb-6">
              {CREDIT_BUREAUS.map(bureau => renderUpload(bureau, true))}
            </div>
          </>
        )}

      <button
        className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 mt-2"
        onClick={handleSubmit}
      >
        Submit Credit Reports
      </button>

      {uploading && (
        <div className="mt-4 text-indigo-600 flex items-center space-x-2">
          <svg className="animate-spin h-5 w-5 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
          <span>Uploading...</span>
        </div>
      )}

      {uploadComplete && !uploading && (
        <div className="mt-4 text-green-600 flex items-center space-x-2 transition-opacity duration-500">
          <svg className="h-6 w-6 text-green-600 animate-bounce" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          <span>Upload complete!</span>
        </div>
      )}
    </div>
  );
}
